import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageProvider";
import { PublicLayout } from "@/components/shared/PublicLayout";
import HomePage from "./pages/HomePage";
import { resolveHostContext } from "@/lib/lp/host";

const AuthRouteShell = lazy(() => import("@/components/shared/AuthRouteShell"));
const DashboardLayout = lazy(() =>
  import("@/components/shared/DashboardLayout").then((m) => ({ default: m.DashboardLayout })),
);
const AdminLayout = lazy(() =>
  import("@/components/admin/AdminLayout").then((m) => ({ default: m.AdminLayout })),
);
const RoleRedirect = lazy(() =>
  import("@/components/shared/RoleRedirect").then((m) => ({ default: m.RoleRedirect })),
);
const ProtectedRoute = lazy(() =>
  import("@/components/shared/ProtectedRoute").then((m) => ({ default: m.ProtectedRoute })),
);
const SellerLandingPageTesterGate = lazy(() =>
  import("@/components/shared/SellerLandingPageTesterGate").then((m) => ({ default: m.SellerLandingPageTesterGate })),
);

const PricingPage = lazy(() => import("./pages/PricingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const WhyChooseUsPage = lazy(() => import("./pages/WhyChooseUsPage"));
const InvestorsPage = lazy(() => import("./pages/InvestorsPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ShowcasePage = lazy(() => import("./pages/ShowcasePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const SystemUpdatePage = lazy(() => import("./pages/SystemUpdatePage"));

const SellerDashboard = lazy(() => import("./pages/seller/SellerDashboard"));
const SellerMarketplace = lazy(() => import("./pages/seller/SellerMarketplace"));
const SellerProductRequests = lazy(() => import("./pages/seller/SellerProductRequests"));
const SellerLandingPages = lazy(() => import("./pages/seller/SellerLandingPages"));
const SellerWallet = lazy(() => import("./pages/seller/SellerWallet"));
const SellerOrders = lazy(() => import("./pages/seller/SellerOrders"));
const SellerCreateOrder = lazy(() => import("./pages/seller/SellerCreateOrder"));
const SellerImportOrders = lazy(() => import("./pages/seller/SellerImportOrders"));
const SellerSettings = lazy(() => import("./pages/seller/SellerSettings"));

const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSellerActivations = lazy(() => import("./pages/admin/AdminSellerActivations"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSystemLogs = lazy(() => import("./pages/admin/AdminSystemLogs"));
const AdminApprovals = lazy(() => import("./pages/admin/AdminApprovals"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductRequests = lazy(() => import("./pages/admin/AdminProductRequests"));
const AdminCallCenter = lazy(() => import("./pages/admin/AdminCallCenter"));
const AdminDigylogIntegration = lazy(() => import("./pages/admin/AdminDigylogIntegration"));
const AdminDeliveryCompanies = lazy(() => import("./pages/admin/AdminDeliveryCompanies"));
const AdminCashSituation = lazy(() => import("./pages/admin/AdminCashSituation"));
const AdminSuppliersOverview = lazy(() => import("./pages/admin/AdminSuppliersOverview"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminLogisticsOverview = lazy(() => import("./pages/admin/AdminLogisticsOverview"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminContactSubmissions = lazy(() => import("./pages/admin/AdminContactSubmissions"));

const SupplierDashboard = lazy(() => import("./pages/supplier/SupplierDashboard"));
const SupplierInventory = lazy(() => import("./pages/supplier/SupplierInventory"));
const SupplierAddProduct = lazy(() => import("./pages/supplier/SupplierAddProduct"));
const SupplierPayouts = lazy(() => import("./pages/supplier/SupplierPayouts"));
const SupplierWallet = lazy(() => import("./pages/supplier/SupplierWallet"));

const SupportTickets = lazy(() => import("./pages/support/SupportTickets"));
const SupportTicketWorkspace = lazy(() => import("./pages/support/SupportTicketWorkspace"));
const InvestorSummary = lazy(() => import("./pages/investor/InvestorSummary"));
const CallCenterDashboard = lazy(() => import("./pages/call-center/CallCenterDashboard"));
const CallCenterLead = lazy(() => import("./pages/call-center/CallCenterLead"));
const CallCenterOverview = lazy(() => import("./pages/call-center/CallCenterOverview"));
const CallCenterWallet = lazy(() => import("./pages/call-center/CallCenterWallet"));
const WarehouseDashboard = lazy(() => import("./pages/logistics/WarehouseDashboard"));
const WarehouseInventory = lazy(() => import("./pages/logistics/WarehouseInventory"));
const WarehouseAfterShip = lazy(() => import("./pages/logistics/WarehouseAfterShip"));
const WarehouseDocuments = lazy(() => import("./pages/logistics/WarehouseDocuments"));
const WarehouseOverview = lazy(() => import("./pages/logistics/WarehouseOverview"));
const WarehouseWallet = lazy(() => import("./pages/logistics/WarehouseWallet"));
const PlaceholderPage = lazy(() => import("./pages/PlaceholderPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SellerLandingPageEditor = lazy(() => import("./pages/seller/SellerLandingPageEditor"));
const SellerLandingPageWizard = lazy(() => import("./pages/seller/SellerLandingPageWizard"));

const SellerLandingPageAnalytics = lazy(() => import("./pages/seller/SellerLandingPageAnalytics"));
const SellerLandingPageDomains = lazy(() => import("./pages/seller/SellerLandingPageDomains"));
const SellerAiConnector = lazy(() => import("./pages/seller/SellerAiConnector"));
const AdminLandingPages = lazy(() => import("./pages/admin/AdminLandingPages"));
const PublicLandingPage = lazy(() => import("./pages/lp/PublicLandingPage"));

const queryClient = new QueryClient();

// Store subdomains and verified custom domains serve landing pages only —
// the app, seller and admin routes stay on the root domain.
const isLandingPageHost = resolveHostContext().kind !== "app";

const RouteFallback = () => (
  <div className="flex min-h-[35vh] items-center justify-center" role="status" aria-live="polite">
    <span className="sr-only">Loading</span>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LanguageProvider>
          <Suspense fallback={<RouteFallback />}>
            {isLandingPageHost ? (
              <Routes>
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/terms" element={<TermsOfService />} />
                <Route path="/lp/:slug" element={<PublicLandingPage />} />
                <Route path="/:slug" element={<PublicLandingPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            ) : (
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
              </Route>

              <Route path="/verify-email" element={<AuthVerify />} />
              <Route path="/auth/verify" element={<AuthVerify />} />
              <Route path="/lp/:slug" element={<PublicLandingPage />} />


              <Route path="/:lang">
                <Route element={<PublicLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="about" element={<AboutPage />} />
                  <Route path="why-choose-us" element={<WhyChooseUsPage />} />
                  <Route path="investors" element={<InvestorsPage />} />
                  <Route path="support" element={<SupportPage />} />
                  <Route path="faq" element={<FAQPage />} />
                  <Route path="terms" element={<TermsPage />} />
                  <Route path="privacy" element={<PrivacyPage />} />
                  <Route path="showcase" element={<ShowcasePage />} />
                  <Route path="partners" element={<PartnersPage />} />
                  <Route path="system-update" element={<SystemUpdatePage />} />
                  <Route path="blog" element={<BlogPage />} />
                  <Route path="blog/:slug" element={<BlogPostPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>

                <Route element={<AuthRouteShell />}>
                  <Route path="login" element={<LoginPage />} />
                  <Route path="signup" element={<SignUpPage />} />
                  <Route path="verify-email" element={<AuthVerify />} />
                  <Route path="auth/verify" element={<AuthVerify />} />
                  <Route path="reset-password" element={<ResetPasswordPage />} />
                  <Route path="oauth/consent" element={<OAuthConsent />} />

                  <Route
                    path="dashboard"
                    element={
                      <ProtectedRoute>
                        <RoleRedirect />
                      </ProtectedRoute>
                    }
                  />

                  <Route element={<DashboardLayout role="seller" />}>
                    <Route path="seller/dashboard" element={<SellerDashboard />} />
                    <Route path="seller/marketplace" element={<SellerMarketplace />} />
                    <Route path="seller/product-requests" element={<SellerProductRequests />} />
                    <Route element={<SellerLandingPageTesterGate />}>
                      <Route path="seller/landing-pages" element={<SellerLandingPages />} />
                      <Route path="seller/landing-pages/domains" element={<SellerLandingPageDomains />} />
                      <Route path="seller/landing-pages/ai" element={<SellerAiConnector />} />
                      <Route path="seller/landing-pages/create" element={<SellerLandingPageWizard />} />
                      <Route path="seller/landing-pages/new" element={<SellerLandingPageEditor />} />
                      <Route path="seller/landing-pages/:id" element={<SellerLandingPageEditor />} />
                      <Route path="seller/landing-pages/:id/analytics" element={<SellerLandingPageAnalytics />} />
                      <Route path="seller/ai-connector" element={<SellerAiConnector />} />
                    </Route>
                    <Route path="seller/wallet" element={<SellerWallet />} />
                    <Route path="seller/orders" element={<SellerOrders />} />
                    <Route path="seller/orders/create" element={<SellerCreateOrder />} />
                    <Route path="seller/orders/import" element={<SellerImportOrders />} />
                    <Route path="seller/settings" element={<SellerSettings />} />
                  </Route>

                  <Route element={<AdminLayout />}>
                    <Route path="admin/overview" element={<AdminOverview />} />
                    <Route path="admin/users" element={<AdminUsers />} />
                    <Route path="admin/seller-activations" element={<AdminSellerActivations />} />
                    <Route path="admin/approvals" element={<AdminApprovals />} />
                    <Route path="admin/staff" element={<PlaceholderPage title="Staff" />} />
                    <Route path="admin/suppliers" element={<AdminSuppliersOverview />} />
                    <Route path="admin/products" element={<AdminProducts />} />
                    <Route path="admin/product-requests" element={<AdminProductRequests />} />
                    <Route path="admin/orders" element={<AdminOrders />} />
                    <Route path="admin/call-center" element={<AdminCallCenter />} />
                    <Route path="admin/logistics" element={<AdminLogisticsOverview />} />
                    <Route path="admin/cash-situation" element={<AdminCashSituation />} />
                    <Route path="admin/wallets" element={<AdminCashSituation />} />
                    <Route path="admin/analytics" element={<PlaceholderPage title="Analytics" />} />
                    <Route path="admin/support" element={<AdminContactSubmissions />} />
                    <Route path="admin/investors" element={<PlaceholderPage title="Investors" />} />
                    <Route path="admin/landing-pages" element={<AdminLandingPages />} />
                    <Route path="admin/settings" element={<AdminSettings />} />
                    <Route path="admin/system-logs" element={<AdminSystemLogs />} />
                    <Route path="admin/marketplace-control" element={<PlaceholderPage title="Marketplace Control" />} />
                    <Route path="admin/integrations/digylog" element={<AdminDigylogIntegration />} />
                    <Route path="admin/delivery-companies" element={<AdminDeliveryCompanies />} />
                    <Route path="admin/blog" element={<AdminBlog />} />
                    <Route path="admin/blog/new" element={<AdminBlogEditor />} />
                    <Route path="admin/blog/:id" element={<AdminBlogEditor />} />
                  </Route>

                  <Route element={<DashboardLayout role="supplier" />}>
                    <Route path="supplier/dashboard" element={<SupplierDashboard />} />
                    <Route path="supplier/inventory" element={<SupplierInventory />} />
                    <Route path="supplier/products/add" element={<SupplierAddProduct />} />
                    <Route path="supplier/wallet" element={<SupplierWallet />} />
                    <Route path="supplier/payouts" element={<SupplierPayouts />} />
                    <Route path="supplier/orders" element={<PlaceholderPage title="Supplier Orders" />} />
                    <Route path="supplier/settings" element={<PlaceholderPage title="Supplier Settings" />} />
                  </Route>

                  <Route element={<DashboardLayout role="support" />}>
                    <Route path="support-console/tickets" element={<SupportTickets />} />
                    <Route path="support-console/tickets/:id" element={<SupportTicketWorkspace />} />
                    <Route path="support-console/customers" element={<PlaceholderPage title="Customers" />} />
                    <Route path="support-console/knowledge-base" element={<PlaceholderPage title="Knowledge Base" />} />
                  </Route>

                  <Route element={<DashboardLayout role="investor" />}>
                    <Route path="investor/summary" element={<InvestorSummary />} />
                  </Route>

                  <Route element={<DashboardLayout role="call-center" />}>
                    <Route path="call-center/dashboard" element={<CallCenterDashboard />} />
                    <Route path="call-center/overview" element={<CallCenterOverview />} />
                    <Route path="call-center/wallet" element={<CallCenterWallet />} />
                    <Route path="call-center/leads/:id" element={<CallCenterLead />} />
                  </Route>

                  <Route element={<DashboardLayout role="logistics" />}>
                    <Route path="logistics/dashboard" element={<WarehouseDashboard />} />
                    <Route path="logistics/after-ship" element={<WarehouseAfterShip />} />
                    <Route path="logistics/documents" element={<WarehouseDocuments />} />
                    <Route path="logistics/inventory" element={<WarehouseInventory />} />
                    <Route path="logistics/wallet" element={<WarehouseWallet />} />
                    <Route path="logistics/overview" element={<WarehouseOverview />} />
                  </Route>

                  <Route element={<DashboardLayout role="account-manager" />}>
                    <Route path="account-manager/dashboard" element={<PlaceholderPage title="Account Manager" />} />
                  </Route>
                </Route>
              </Route>

              <Route element={<PublicLayout />}>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            )}
          </Suspense>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
